import React, {
  ReactNode,
} from 'react';
import {
  useFormContext,
} from 'react-hook-form';
import CloseIcon from '@mui/icons-material/Close';
import IconButton from '@mui/material/IconButton';
import DatePicker from 'core/components/DateTimePicker/SingleDatePicker';
import Wrapper from 'pages/Dashboard/components/Input/Wrapper';
import Controller from 'core/components/FormController';
import ToolTip from 'core/components/Tooltip';
import {
  FieldComponentProps,
} from 'pages/Dashboard/pages/Charts/pages/Details/components/FieldsArrayWrapper';
import Select from 'core/components/Select';
import {
  useClaimSettings,
  useGetAccidentDatesByPatientId,
} from 'pages/Dashboard/pages/Billing/hooks';
import {
  ClaimDateQualifierSettingDTO,
} from 'dtos';
import dayjs, {
  Dayjs,
} from 'utils/dayjs';
import clsx from 'clsx';
import {
  formatDate,
} from 'utils/date';
import Tag from 'core/components/Tag';

type ShortcutsProps = {
  onChange?: (date: Dayjs) => void;
  accidentDates?: string[];
  value?: Dayjs;
  children: ReactNode;
};

function AccidentDateShortcuts({
  onChange,
  children,
  accidentDates = [],
  value,
}: ShortcutsProps) {
  return (
    <>
      <div className="flex flex-col gap-4 pt-4 text-gray-700 border-r border-gray-200 overflow-y-hidden h-[320px] w-32">
        <div className="font-semibold text-xs px-4">
          Previous Accident Dates
        </div>
        <div className="flex flex-col gap-2 flex-1 overflow-y-auto w-full px-4 pb-4">
          {accidentDates?.map((accidentDate) => {
            const date = dayjs(accidentDate);
            const isSelected = value?.isSame(date, 'day');
            return (
              <Tag
                key={accidentDate}
                defaultColor={clsx({
                  '!bg-blue-500': isSelected,
                  '!bg-gray-200': !isSelected,
                })}
                className="text-center font-semibold"
                onClick={() => { onChange?.(date); }}
              >
                {formatDate(date)}
              </Tag>
            );
          })}
        </div>
      </div>
      {children}
    </>
  );
}

export default function DateItem({
  index,
  onDelete,
  prefix,
}: FieldComponentProps) {
  const { control, watch } = useFormContext();
  const key = `${prefix}.${index}`;
  const showLabel = index === 0;
  const { dateQualifierSettings } = useClaimSettings();
  const [patientId, dateQualifier] = watch(['patientId', `${key}.item`]);
  const isAccidentDateQualifier = dateQualifier?.qualifier === '439';

  const { data: accidentDates = [] } = useGetAccidentDatesByPatientId(
    patientId,
    isAccidentDateQualifier,
  );

  return (
    <div className="flex items-end gap-3 w-full">
      <Controller
        name={`${key}.item`}
        control={control}
        render={({ field }) => (
          <Select<ClaimDateQualifierSettingDTO>
            {...field}
            getValue={(option) => option.qualifier ?? ''}
            getLabel={({ qualifier, name, box }) => `${box ? ` Box ${box} - ` : ''}${name} [${qualifier}]`}
            options={dateQualifierSettings ?? []}
            placeholder="Select"
            className="w-1/2"
            id="date-type"
            muiClasses={{ select: '!py-[7px]' }}
            label={showLabel ? 'Date Qualifier' : ''}
          />
        )}
      />
      <Controller
        name={`${key}.date`}
        control={control}
        render={({ field: { onChange, value, ...field } }) => (
          <Wrapper
            id="other-date"
            className="w-fit"
            label={showLabel ? 'Date Value' : ''}
          >
            <DatePicker
              {...field}
              value={value}
              onChange={onChange}
              showDateButtons={false}
              {...(isAccidentDateQualifier && accidentDates?.length > 0 ? {
                slots: { desktopPaperContent: AccidentDateShortcuts } as object,
                slotProps: {
                  desktopPaper: { sx: { display: 'flex', flexDirection: 'row' } },
                  desktopPaperContent: { accidentDates, value },
                } as object,
              } : {})}
            />
          </Wrapper>
        )}
      />
      <ToolTip title="Remove">
        <span>
          <IconButton onClick={() => { onDelete(index); }}>
            <CloseIcon className="!w-4 !h-4" />
          </IconButton>
        </span>
      </ToolTip>
    </div>
  );
}
